import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PrismButton from '../prism/PrismButton';
import { ArrowIcon } from '../prism/icons';

// An underlined field in elebyte.io's booking-form idiom (no box): the line lights cyan on focus.
//   variant="hero"    — large type and a prism SEARCH pill, for the home and knowledge base heads
//   variant="compact" — small, for the top bar
//   default           — mid-size, the submit arrow appears once there's something to search
const SIZES = {
  hero: { fontSize: 'clamp(19px, 2.1vw, 26px)', fontWeight: 600, py: '12px', icon: 26 },
  compact: { fontSize: 14, fontWeight: 500, py: '6px', icon: 18 },
  default: { fontSize: 17, fontWeight: 500, py: '10px', icon: 22 },
};

// The underlined look, shared with the search page's own field.
export const underlineFieldSx = (size = SIZES.default) => ({
  '& .MuiInputBase-root': {
    fontSize: size.fontSize,
    fontWeight: size.fontWeight,
    letterSpacing: '-0.01em',
    color: '#fff',
  },
  '& .MuiInputBase-input': { py: size.py },
  '& .MuiInputBase-input::placeholder': { color: 'rgba(255, 255, 255, 0.6)', opacity: 0.64, fontWeight: 400 },
  '& .MuiInput-underline:before': { borderBottomColor: 'rgba(255, 255, 255, 0.24)' },
  '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottomColor: 'rgba(255, 255, 255, 0.5)' },
  '& .MuiInput-underline:after': { borderBottomColor: 'primary.main', borderBottomWidth: 2 },
  '& .MuiInputBase-root.Mui-focused .sp-search-glyph': { color: 'primary.main' },
});

export { SIZES as SEARCH_SIZES };

function SearchBar({ placeholder = 'Search help articles...', fullWidth = false, variant = 'default' }) {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();
  const size = SIZES[variant] || SIZES.default;
  const hero = variant === 'hero';

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim().length >= 2) {
      navigate(`/kb/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const width = fullWidth || hero ? '100%' : variant === 'compact' ? 220 : 280;

  return (
    <form onSubmit={handleSubmit} role="search" className={hero ? 'sp-search' : undefined}>
      <TextField
        variant="standard"
        placeholder={placeholder}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        inputProps={{ 'aria-label': placeholder || 'Search' }}
        sx={{ width, flex: hero ? 1 : undefined, ...underlineFieldSx(size) }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className="sp-search-glyph" sx={{ color: 'text.secondary', fontSize: size.icon, transition: 'color 0.2s ease' }} />
            </InputAdornment>
          ),
          endAdornment: !hero && query && (
            <InputAdornment position="end">
              <IconButton
                type="submit"
                size="small"
                aria-label="Submit search"
                sx={{ color: 'primary.main' }}
              >
                <ArrowIcon size={18} />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {hero && <PrismButton type="submit" label="Search" icon={<ArrowIcon />} size="lg" />}
    </form>
  );
}

export default SearchBar;
